import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { mq, canUseDOM } from '$lib/helpers';
import { SpotLayout } from '../components/spot-layout';
import DangerouslySetHtmlContent from '~/utils/dangerouslySetHtmlContent';

export type ScriptSpotProps = {
    script: string;
};

// Etrack
const etrackHeightSmallest = '2100px';
const etrackHeightMobile = '1200px';
const etrackHeightTablet = '800px';
const etrackHeightDesktop = '561px';
const etrackClickHeight = '1200px';

//Saldo
const saldoHeightSmallest = '700px';
const saldoHeightSmallMobile = '380px';
const saldoHeightMobile = '440px';
const saldoHeightTablet = '468px';
const saldoHeightSmallDesktop = '531px';
const saldoHeightDesktop = '601px';

// Yes this spot exists unfortunately
export const ScriptSpot: FC<ScriptSpotProps> = ({ script, ...props }: ScriptSpotProps) => {
    const [etrackClicked, setEtrackClicked] = useState(false);

    // Fix for customer service height issues
    // The resizer doesn't work properly in the SPA after initial
    // We implement a fixed height and create a blur event to track clicks in IFrame
    // We do this because the content expands when clicking items in the IFrame
    useEffect(() => {
        if (canUseDOM() && script.includes('etrack1')) {
            window.focus(); //force focus on the currenct window;
            window.addEventListener('blur', function () {
                setEtrackClicked(true);
            });
        }
    }, []);

    const content = useMemo(() => {
        if (script.includes('etrack1')) {
            const Etrack = dynamic(() => import('../../scripts/etrack'), { ssr: false });

            return (
                <>
                    <Etrack />
                    <EtrackContainer clicked={etrackClicked}>
                        <div dangerouslySetInnerHTML={{ __html: script }} />
                    </EtrackContainer>
                </>
            );
        } else if (script.includes('saldo-indeksretail')) {
            return (
                <SaldoContainer>
                    <div dangerouslySetInnerHTML={{ __html: script }} />
                </SaldoContainer>
            );
        } else {
            return <DangerouslySetHtmlContent html={script} />;
        }
    }, [script, etrackClicked]);

    return <SpotLayout {...props}>{content}</SpotLayout>;
};

const EtrackContainer = styled.div<{ clicked?: boolean }>(({ clicked }) => ({
    '& iframe': {
        minHeight: `calc(${etrackHeightSmallest} + ${clicked ? etrackClickHeight : '0px'})`,
        [mq('xs', 'sm')]: {
            minHeight: `calc(${etrackHeightMobile} + ${clicked ? etrackClickHeight : '0px'})`,
        },
        [mq('sm', 'md')]: {
            minHeight: `calc(${etrackHeightTablet} + ${clicked ? etrackClickHeight : '0px'})`,
        },
        [mq('md')]: {
            minHeight: `calc(${etrackHeightDesktop} + ${clicked ? etrackClickHeight : '0px'})`,
        },
    },
}));

const SaldoContainer = styled.div({
    '& iframe': {
        minHeight: saldoHeightSmallest,
        [mq(640, 680)]: {
            minHeight: saldoHeightSmallMobile,
        },
        [mq(680, 'md')]: {
            minHeight: saldoHeightMobile,
        },
        [mq('md', 'ml')]: {
            minHeight: saldoHeightTablet,
        },
        [mq('ml', 'lg')]: {
            minHeight: saldoHeightSmallDesktop,
        },
        [mq('lg')]: {
            minHeight: saldoHeightDesktop,
        },
    },
});
